import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import '../../styles/resources/Resources_simple.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

function Resources() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clickStats, setClickStats] = useState({ totalClicks: 0, totalResources: 0 });
  
  const selectedCategory = searchParams.get('category') || 'all';
  const selectedSort = searchParams.get('sort') || null;

  const categories = [
    { value: 'all', label: 'All Resources' },
    { value: 'Templates & Tools', label: 'Templates & Tools' },
    { value: 'Learning Hub', label: 'Learning Hub' },
    { value: 'Academic Resources', label: 'Academic Resources' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch resources
        const resourcesResponse = await fetch(`${BACKEND_URL}/api/resources`);
        if (!resourcesResponse.ok) {
          throw new Error(`HTTP error! status: ${resourcesResponse.status}`);
        }
        const resourcesData = await resourcesResponse.json();
        setResources(resourcesData.resources || resourcesData || []);

        // Fetch click statistics
        const statsResponse = await fetch(`${BACKEND_URL}/api/resources/stats`);
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setClickStats(statsData);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCategoryChange = (category) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (category === 'all') {
      newParams.delete('category');
    } else {
      newParams.set('category', category);
    }
    
    setSearchParams(newParams);
  };

  const handleSortChange = (sortType) => {
    const newParams = new URLSearchParams(searchParams);
    
    // Toggle off if same sort is selected
    if (selectedSort === sortType) {
      newParams.delete('sort');
    } else {
      newParams.set('sort', sortType);
    }
    
    setSearchParams(newParams);
  };

  const handleResourceClick = async (resourceId, resourceLink) => {
    // Navigate to the link in the current tab
    window.location.href = resourceLink;
    
    // Update local stats optimistically
    setClickStats(prev => ({
      ...prev,
      totalClicks: prev.totalClicks + 1
    }));
    
    // Track the click in background (don't await)
    fetch(`${BACKEND_URL}/api/resources/${resourceId}/click`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    }).catch(err => {
      console.error('Click tracking failed (non-blocking):', err);
      // Revert the optimistic update if tracking failed
      setClickStats(prev => ({
        ...prev,
        totalClicks: Math.max(0, prev.totalClicks - 1)
      }));
    });
  };

  const filteredResources = (() => {
    // First filter by category
    let filtered = selectedCategory === 'all' 
      ? resources 
      : resources.filter(resource => resource.category === selectedCategory);
    
    // Then apply sorting
    if (selectedSort === 'newest') {
      filtered = [...filtered].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (selectedSort === 'popular') {
      filtered = [...filtered].sort((a, b) => (b.clickCount || 0) - (a.clickCount || 0));
    }
    
    return filtered;
  })();

  if (loading) return <div className="loading">Loading resources...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="resources-page">
      <Helmet>
        <title>NHHS Analytics | Resources Hub</title>
      </Helmet>
      {/* Hero Section */}
      <section className="resources-hero">
        <div className="resources-hero-content">
          <h1>Resources Hub</h1>
          <p>
            Comprehensive collection of professional tools, templates, and learning materials 
            designed to accelerate your analytics capabilities and drive data-informed decisions.
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-number">{resources.length}</div>
              <div className="stat-label">Resources</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{clickStats.totalClicks || 0}+</div>
              <div className="stat-label">Times Used</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">Real-Time</div>
              <div className="stat-label">Analytics</div>
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="filter-section">
          <div className="filters-container">
            <div className="filter-buttons">
              {categories.map(category => (
                <button
                  key={category.value}
                  className={`filter-btn ${selectedCategory === category.value ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(category.value)}
                >
                  {category.label}
                </button>
              ))}
            </div>
            
            <div className="resource-count">
              {filteredResources.length} resource{filteredResources.length !== 1 ? 's' : ''}
            </div>
          </div>
          
          <div className="sort-buttons">
            <button
              className={`sort-btn ${selectedSort === 'newest' ? 'active' : ''}`}
              onClick={() => handleSortChange('newest')}
            >
              Newest
            </button>
            <button
              className={`sort-btn ${selectedSort === 'popular' ? 'active' : ''}`}
              onClick={() => handleSortChange('popular')}
            >
              Most Popular
            </button>
          </div>
        </div>

        <div className="resources-grid">
          {filteredResources.map(resource => (
            <div key={resource._id} className="resource-card">
              <div className="resource-header">
                <h3>{resource.title}</h3>
                <span className="resource-category">{resource.category}</span>
              </div>
              <div className="resource-content">
                <p className="resource-description">{resource.description}</p>
                {resource.link && (
                  <button 
                    onClick={() => handleResourceClick(resource._id, resource.link)}
                    className="resource-link"
                  >
                    Visit Resource →
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredResources.length === 0 && !loading && (
          <div className="no-resources">
            <p>No resources found for the selected category.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Resources;
